import { showToast } from "@certn/cascadia-react";
import { t } from "i18next";

export interface DocumentUrlsResponse {
  pdf_report?: string | null;
  csv_report?: string | null;
  consent_documents?: string[];
}

/**
 * Downloads a file from a URL by creating a blob and triggering a download
 * @param url - The URL of the file to download
 * @param filename - The name to use for the downloaded file
 * @returns Promise that resolves to true if download succeeded, false if it fell back to window.open
 */
export async function downloadFile(
  url: string,
  filename: string
): Promise<boolean> {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
    return true;
  } catch (error) {
    console.error(`Failed to download ${filename}:`, error);
    window.open(url, "_blank");
    return false;
  }
}

/**
 * Downloads all available documents for a legacy case
 * @param documentUrls - The document URLs response from the API
 * @param reportId - The report ID to use in filenames
 * @returns Promise that resolves when all downloads are complete
 */
export async function downloadAllDocuments(
  documentUrls: DocumentUrlsResponse,
  reportId: string
): Promise<void> {
  // Build download tasks array using functional methods
  const downloadTasks: Array<{ url: string; filename: string }> = [
    ...(documentUrls.pdf_report
      ? [
          {
            url: documentUrls.pdf_report,
            filename: `report-${reportId}.pdf`,
          },
        ]
      : []),
    ...(documentUrls.csv_report
      ? [
          {
            url: documentUrls.csv_report,
            filename: `report-${reportId}.csv`,
          },
        ]
      : []),
    ...(documentUrls.consent_documents &&
    documentUrls.consent_documents.length > 0
      ? documentUrls.consent_documents.map((url, index) => ({
          url,
          filename:
            documentUrls.consent_documents!.length > 1
              ? `consent-form-${reportId}-${index + 1}.pdf`
              : `consent-form-${reportId}.pdf`,
        }))
      : []),
  ];

  if (downloadTasks.length === 0) {
    showToast(t("No documents available to download"), {
      type: "info",
    });
    return;
  }

  try {
    const results = await Promise.allSettled(
      downloadTasks.map((task) => downloadFile(task.url, task.filename))
    );

    const allSucceeded = results.every(
      (result) => result.status === "fulfilled" && result.value === true
    );

    if (allSucceeded) {
      showToast(t("All documents downloaded successfully"), {
        type: "success",
      });
    } else {
      showToast(t("Unable to download all documents"), { type: "error" });
    }
  } catch (error) {
    console.error("Failed to download all documents:", error);
    showToast(t("Unable to download all documents"), { type: "error" });
  }
}

/**
 * Downloads the PDF report for a legacy case
 * @param documentUrls - The document URLs response from the API
 * @param reportId - The report ID to use in filename
 */
export async function downloadPDFReport(
  documentUrls: DocumentUrlsResponse,
  reportId: string
): Promise<void> {
  if (documentUrls.pdf_report) {
    const succeeded = await downloadFile(
      documentUrls.pdf_report,
      `report-${reportId}.pdf`
    );
    if (!succeeded) {
      showToast(t("Unable to download report PDF"), { type: "error" });
    }
  } else {
    showToast(t("PDF report is not available for this case"), {
      type: "error",
    });
  }
}

/**
 * Downloads the CSV report for a legacy case
 * @param documentUrls - The document URLs response from the API
 * @param reportId - The report ID to use in filename
 */
export async function downloadCSVReport(
  documentUrls: DocumentUrlsResponse,
  reportId: string
): Promise<void> {
  if (documentUrls.csv_report) {
    const succeeded = await downloadFile(
      documentUrls.csv_report,
      `report-${reportId}.csv`
    );
    if (!succeeded) {
      showToast(t("Unable to download report CSV"), { type: "error" });
    }
  } else {
    showToast(t("CSV report is not available for this case"), {
      type: "error",
    });
  }
}

/**
 * Downloads all consent documents for a legacy case
 * @param documentUrls - The document URLs response from the API
 * @param reportId - The report ID to use in filenames
 */
export async function downloadConsentDocuments(
  documentUrls: DocumentUrlsResponse,
  reportId: string
): Promise<void> {
  if (
    documentUrls.consent_documents &&
    documentUrls.consent_documents.length > 0
  ) {
    try {
      const downloadTasks = documentUrls.consent_documents.map(
        (url, index) => ({
          url,
          filename:
            documentUrls.consent_documents!.length > 1
              ? `consent-form-${reportId}-${index + 1}.pdf`
              : `consent-form-${reportId}.pdf`,
        })
      );

      const results = await Promise.allSettled(
        downloadTasks.map((task) => downloadFile(task.url, task.filename))
      );

      const allSucceeded = results.every(
        (result) => result.status === "fulfilled" && result.value === true
      );

      if (allSucceeded) {
        showToast(t("Consent documents downloaded successfully"), {
          type: "success",
        });
      } else {
        showToast(t("Unable to download consent form"), { type: "error" });
      }
    } catch (error) {
      console.error("Failed to download consent documents:", error);
      showToast(t("Unable to download consent form"), { type: "error" });
    }
  } else {
    showToast(t("Consent form is not available for this case"), {
      type: "error",
    });
  }
}
